import React, { useCallback, useState } from 'react'
import { UseStepFunc, defaultStepFormProps } from '../types.d'
import { OnboardingCredentialsForm } from './OnboardingCredentialsForm'
import { Route, Switch } from 'react-router-dom'
import * as routing from '../../constants/routes'
import { IShopWholesaler, OnboardingStepKey } from '@cbgms/api'
import { AdditionalWholesaler } from '../SetupWholesalersStep/types.d'
import { UseWholesalerValidatorResult } from 'app/auth/components/hooks/use-wholesaler-validator-builder'
import { useDispatch } from 'react-redux'
import { setOnboardingToken } from 'app/auth/state/actions'
import { useCompleteOnSubmit } from '../use-complete-on-submit'
import { useShouldExecuteStep } from '../use-should-execute-step'
import { useDefaultSkipSetting } from '../use-default-skip-setting'
import { useShouldRunInBackground } from '../use-should-run-in-background'
import { useStepData } from '../use-step-data'
import GenericDuplicationError from 'app/auth/components/onboarding/steps/shared/GenericDuplicationError'
import { InviteUserToOtherShopWithoutLoginCredentials } from 'app/auth/components/onboarding/steps/OnboardingWholesalerCredentialsStep/InviteUserToOtherShopWithoutLoginCredentials/InviteuserToOtherShopWithoutLoginCredentials'
import AlreadyRegisteredMGID from './AlreadyRegisteredMGID'
import AlreadyRegisteredAxNumber from './AlreadyRegisteredAxNumber'

interface IStepProps {
  basePath: string
  onSubmit: (result: UseWholesalerValidatorResult) => void
  wholesaler: IShopWholesaler
}

interface IStepInputProps {
  basePath: string
}

interface StepOutput {
  wholesalerCredentials: AdditionalWholesaler | null
}

export const useOnboardingWholesalerCredentialsStep: UseStepFunc<IStepProps, IStepInputProps, StepOutput> = ({
  basePath,
  onboardingInfo
}) => {
  const wholesaler = onboardingInfo.wholesaler!
  const stepName = OnboardingStepKey.WholesalerCredentialsLogin
  const stepData = useStepData(stepName, onboardingInfo)
  const shouldExecuteStep = useShouldExecuteStep(stepName, onboardingInfo)
  const shouldRunInBackground = useShouldRunInBackground(stepName, onboardingInfo)

  const hideSkip = useDefaultSkipSetting(onboardingInfo)
  const [wholesalerCredentials, setValidatedWholesalerCredentials] = useState<AdditionalWholesaler | null>(null)
  const dispatch = useDispatch()
  const onStepSubmit = useCallback(
    async (result: UseWholesalerValidatorResult) => {
      if (result?.token?.AuthenticationResult?.IDToken) {
        // set a temporary token, so the next api requests have a token they can use.
        await dispatch(setOnboardingToken(result.token?.AuthenticationResult))
      }

      setValidatedWholesalerCredentials({ Credentials: result.Credentials, WholesalerUUID: wholesaler?.UUID || '', wholesaler })
    },
    [dispatch, setValidatedWholesalerCredentials, wholesaler]
  )
  const complete = useCompleteOnSubmit(onStepSubmit)

  return {
    Component: OnboardingViaWholesalerCredentialsStep,
    CTAComponent: null,
    isCompleted: complete.isCompleted,
    showInStepper: false,
    showStepperHeader: false,
    step: stepName,
    stepData,
    shouldExecuteStep,
    shouldRunInBackground,
    hideSkip,
    stepProps: {
      duplicationCheck: stepData.DuplicationCheck || [],
      basePath,
      onSubmit: complete.onSubmit,
      wholesaler
    },
    persister: {
      onPersist: async () => {}, // do nothing, creating order account is done in different step. this is just to get a temp token.
      persistOrder: 0
    },
    wholesalerCredentials // we expose the credentials for the next steps
  }
}

export const OnboardingViaWholesalerCredentialsStep: React.FC<IStepProps & defaultStepFormProps> = ({
  basePath,
  onSubmit,
  wholesaler,
  ...rest
}) => {
  return (
    <Switch>
      <Route path={routing.pathDuplicationError(routing.pathWholesalerCredentials(basePath))} component={GenericDuplicationError} />
      <Route path={routing.pathAlreadyRegisteredMGID(basePath)} component={AlreadyRegisteredMGID} />
      <Route path={routing.pathAlreadyRegisteredAxNumber(basePath)} component={AlreadyRegisteredAxNumber} />
      <Route
        path={routing.pathInviteUserToOtherShop(routing.pathWholesalerCredentials(basePath))}
        component={InviteUserToOtherShopWithoutLoginCredentials}
      />
      <Route
        path={routing.pathWholesalerCredentials(basePath)}
        render={() => <OnboardingCredentialsForm basePath={basePath} onSubmit={onSubmit} wholesaler={wholesaler} {...rest} />}
      />
    </Switch>
  )
}
